<template>
  <div class="relative flex-1">
    <FormField
      ref="formField"
      :value="passengersText"
      :title="$t('search-form.passengers-select.title')"
      readonly
      :placeholder="
        $t('search-form.passengers-select.adult-count', { count: 2 })
      "
      :is-active="isActive"
      @onClick="emit('onFieldSelect')"
      @onFocus="emit('onFieldSelect')"
    />

    <section
      v-if="isActive"
      ref="dropdown"
      v-on-click-outside="onClickOutsideHandler"
      class="dropdown-form-shadow absolute z-30 mt-2 flex w-80 flex-1 flex-col divide-x overflow-hidden rounded-xl bg-white pt-2"
    >
      <PassengersCounterContainer
        :adult-count="passengers.ADT"
        :children="passengers.children"
        :is-max-adults="isMaxAdultCount"
        :is-max-children="isMaxChildrenCount"
        :is-max-passengers="isMaxPassengersCount"
        @onAdultCountUpdate="emit('onAdultsChange', $event)"
        @onChildrenUpdate="emit('onChildrenChange', $event)"
        @onError="emit('onError', $event)"
      />
    </section>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { useTranslation } from 'i18next-vue';
  import { vOnClickOutside } from '@vueuse/components';
  import type { Tourflow } from '@aviata/platform-types';

  import { PassengersCounterContainer } from '@widgets/passengers-counter-container';
  import type {
    PassengerChildrenMap,
    PassengersState,
  } from '@entities/passengers-state';
  import type { OnOutsideClickHandler } from '@shared/types';

  import FormField from '../FormField.vue';

  const { t } = useTranslation();

  const dropdown = ref<HTMLElement | null>(null);
  const formField = ref<HTMLElement | null>(null);

  const props = defineProps<{
    isActive: boolean;
    isMaxAdultCount: boolean;
    isMaxChildrenCount: boolean;
    isMaxPassengersCount: boolean;
    passengers: PassengersState;
    selectedTownFrom?: Tourflow.TownFrom;
  }>();

  const emit = defineEmits<{
    onFieldSelect: [];
    onClickOutsideField: [];
    onError: [errorText: string];
    onAdultsChange: [count: number];
    onChildrenChange: [children: PassengerChildrenMap];
  }>();

  const passengersText = computed(() => {
    const { ADT, CHD, INF } = props.passengers;

    if (ADT > 0 && CHD === 0 && INF === 0) {
      return t('search-form.passengers-select.adult-count', {
        count: ADT,
      });
    }

    const paxShortText = [
      { count: ADT, tKey: 'search-form.passengers-select.adult-count-short' },
      { count: CHD, tKey: 'search-form.passengers-select.child-count' },
      { count: INF, tKey: 'search-form.passengers-select.infant-count' },
    ];

    const nonZeroPax = paxShortText.filter(({ count }) => count > 0);
    return nonZeroPax.map(({ count, tKey }) => t(tKey, { count })).join(', ');
  });

  const onClickOutsideHandler: OnOutsideClickHandler = [
    () => {
      emit('onClickOutsideField');
    },
    { ignore: [formField] },
  ];
</script>
